import { CoreTextView } from '@core/widgets';
import { useApp, useTriggerAction, useUpdateWidgetValue } from '@libs/core';
import { ActionActionTypeEnum, TBalanceItem, TWidget, TWidgetCommonOption, TWidgetProps } from '@libs/types';
import React, { useCallback } from 'react';
import { TBalanceData, TConsumerListData } from '../../types/data';
import { ListEnum, RowEnum } from '../../types/enums';
import { TListOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { ButtonRowView } from '../Buttons/ButtonRowView';
import { ListSelectView } from '../List/select/ListSelectView';
import { SeparatorView } from '../Separator/SeparatorView';
import { PopupComponent } from '../UI/Popup/PopupComponent';
import { convertBalanceInToListItem } from './utils/balanceUtils';

type TBalanceBottonSheetProps = {
    value: string;
    storageKey: string;
} & TWidgetProps<TBalanceData> &
    Pick<TWidgetCommonOption, 'parentStyleNames'>;
const SelectBalanceBottomSheet: React.FC<TBalanceBottonSheetProps> = ({ widget, value, parentStyleNames, storageKey }) => {
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const { setPopup } = useApp();
    const data = widget.data ?? {};
    const messages = data.messages ?? {};
    const isShowBalances = data.balances?.length > 1;

    const buttonClickHandel = useCallback(
        (actionName: string) => {
            triggerActionsByNameAndType(ActionActionTypeEnum.onClick, actionName).then();
            setPopup(null);
        },
        [setPopup, triggerActionsByNameAndType],
    );

    const rowButtonParentStyleNames = parentStyleNames ?? [];

    const getButtons = () => {
        const buttons = [];
        if (data.canAddCurrency) {
            buttons.push(
                <ButtonRowView
                    version={widget.version}
                    parentStyleNames={rowButtonParentStyleNames}
                    onClick={() => buttonClickHandel('addAnotherCurrency')}
                    label={messages.addBalanceButtonTitle}
                    iconName={'ic_plus'}
                />,
            );
        }
        if (data.canExchange) {
            buttons.push(
                <ButtonRowView
                    version={widget.version}
                    parentStyleNames={rowButtonParentStyleNames}
                    onClick={() => buttonClickHandel('exchange')}
                    label={messages.exchangeButtonTitle}
                    iconName={'ic_arrows_recurring'}
                />,
            );
        }
        if (data.canGetPaysendCard) {
            buttons.push(
                <ButtonRowView
                    version={widget.version}
                    parentStyleNames={rowButtonParentStyleNames}
                    onClick={() => buttonClickHandel('getPaysendCard')}
                    label={messages.getCardButtonTitle}
                    iconName={'ic_card_plus'}
                />,
            );
        }
        return buttons;
    };

    const getBalances = (balanceWidget: TWidget<TBalanceData>) => {
        const widgetList: TWidget<TConsumerListData, TListOption> = {
            ...balanceWidget,
            id: ListEnum.SELECT,
            alternativeId: ListEnum.SELECT,
            data: {
                value: value,
                items: convertBalanceInToListItem(balanceWidget.data.balances, value as string),
            },
            widgets: [],
        };
        return (
            <>
                <CoreTextView
                    text={messages?.selectBalanceTitle}
                    styleProps={{ ...TextStyle.main, textAlign: 'center', margin: { top: 48, bottom: 64, right: 32, left: 32 } }}
                />
                <SeparatorView />
                <ListSelectView
                    widget={widgetList}
                    rowId={RowEnum.SELECTOR_EXTRA}
                    isOpenInModal
                    onSelectItem={(_uid, item) => {
                        triggerActionsByType(ActionActionTypeEnum.onItemSelected).then();
                        localStorage.setItem(storageKey, (item as TBalanceItem).currencyIso as string);
                        updateWidgetValue(item.value);
                        setPopup(null);
                    }}
                    isHidden={false}
                    isDisabled={false}
                    dataForListSelect={widgetList.data}
                    parentStyleNames={parentStyleNames}
                />
            </>
        );
    };

    return <PopupComponent internalContent={isShowBalances ? getBalances(widget) : false} externalContent={getButtons()} withoutSeparator />;
};

export default SelectBalanceBottomSheet;
